import { Button } from "@/components/ui/button"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { useMemo, useState } from "react"
import { toastApiError } from "../../api/apiError"
import {
  usePatchQuestionnaire,
  type QuestionnaireRow,
} from "../../api/customerRequestsApi"
import { useRegulationSettings } from "../../api/regulationSettingsApi"
import { useCanViewOrgRegulations } from "../../hooks/useCanViewOrgRegulations"
import {
  INQUIRY_REGULATION_KEYS,
  inquiryRegulationLabels,
  toggleRegulationKey,
} from "../../utils/inquiryScope"
import { McPill } from "../shared/McPill"

/** A customer request's regulations as canonical keys: one muted pill each, short
 *  label with the full name on hover. */
export function RequestRegulationPills({ keys }: { keys: readonly string[] }) {
  const { translate } = useTranslation()
  return (
    <>
      {inquiryRegulationLabels(keys, translate).map((regulation) => (
        <McPill key={regulation.key} tone="muted" title={regulation.title}>
          {regulation.label}
        </McPill>
      ))}
    </>
  )
}

/** What the request names that matches no regulation of the declaration (FMD, SCIP,
 *  CMRT, or a spelling not recognised yet): shown as written, once each, and marked as
 *  such, never guessed onto a regulation. */
export function UnmappedFrameworkPills({
  values,
}: {
  values: readonly string[]
}) {
  const { translate } = useTranslation()
  return (
    <>
      {[...new Set(values)].map((value) => (
        <McPill
          key={value}
          tone="muted"
          title={translate("MC_Q_REGULATION_UNMAPPED")}
          className="border-dashed opacity-70"
        >
          {value}
        </McPill>
      ))}
    </>
  )
}

/** Says why REACH Annex XVII comes with PFAS (the rule Settings enforces), where the
 *  coupling would otherwise happen silently. In the picker it also says that dropping
 *  Annex XVII drops PFAS; read-only it only says why Annex XVII is there. */
function PfasNeedsAnnexXviiNote({
  keys,
  readOnly = false,
}: {
  keys: readonly string[]
  readOnly?: boolean
}) {
  const { translate } = useTranslation()
  if (!keys.includes("PFAS")) return null
  return (
    <p className={cn("text-[11px] text-grey5", readOnly && "text-right")}>
      {translate(
        readOnly
          ? "MC_Q_REGULATIONS_PFAS_BRINGS_XVII"
          : "MC_Q_REGULATIONS_PFAS_NEEDS_XVII",
      )}
    </p>
  )
}

/** Why a regulation is greyed out in the picker, from the org's Settings: not in
 *  its set, or no declaration content yet (it gets blocked later in conformity).
 *  Empty while the settings are unknown or hidden from this user. */
function useRegulationPickerHints(): Map<string, TranslationKeys> {
  const canView = useCanViewOrgRegulations()
  const { data } = useRegulationSettings({ enabled: canView })
  return useMemo(() => {
    const hints = new Map<string, TranslationKeys>()
    if (!canView || !data) return hints
    const inSet = new Set<string>()
    const withContent = new Set<string>()
    for (const row of data.regulations) {
      for (const key of row.frameworkKeys) {
        if (row.subscribed) inSet.add(key)
        if (row.pickable) withContent.add(key)
      }
    }
    for (const key of INQUIRY_REGULATION_KEYS) {
      if (!withContent.has(key)) hints.set(key, "MC_Q_REGULATION_NO_CONTENT")
      else if (!inSet.has(key))
        hints.set(key, "MC_Q_REGULATION_NOT_IN_SETTINGS")
    }
    return hints
  }, [canView, data])
}

/** The reviewer's choice of regulations: one toggle per regulation in catalogue
 *  order (the mirror of backend FRAMEWORK_ORDER), the picked ones highlighted.
 *  Coupled like Settings (picking PFAS picks REACH Annex XVII), with a note saying so.
 *  Regulations outside the org's set or without declaration content are greyed out,
 *  but stay pickable: the hint is advisory, since the request records what the
 *  customer asked. */
export function RegulationKeysPicker({
  value,
  onChange,
  disabled,
}: {
  value: readonly string[]
  onChange: (keys: string[]) => void
  disabled?: boolean
}) {
  const { translate } = useTranslation()
  const hints = useRegulationPickerHints()
  const picked = new Set(value)
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap gap-1.5">
        {inquiryRegulationLabels(INQUIRY_REGULATION_KEYS, translate).map(
          (regulation) => {
            const on = picked.has(regulation.key)
            const hint = hints.get(regulation.key)
            return (
              <button
                key={regulation.key}
                type="button"
                aria-pressed={on}
                title={
                  hint
                    ? `${regulation.title} · ${translate(hint)}`
                    : regulation.title
                }
                disabled={disabled}
                onClick={() =>
                  onChange(toggleRegulationKey(value, regulation.key))
                }
                className={cn(
                  "inline-flex h-[22px] items-center rounded-full border px-2 text-[11.5px] font-medium transition-colors disabled:opacity-50",
                  on
                    ? "border-yellow1 bg-yellow1/10 text-yellow1"
                    : "border-dashed border-grey4 text-grey5 hover:text-white",
                  hint && !on && "opacity-50",
                )}
              >
                {regulation.label}
              </button>
            )
          },
        )}
      </div>
      <PfasNeedsAnnexXviiNote keys={value} />
      {hints.size > 0 && (
        <p className="text-[11px] text-grey5">
          {translate("MC_Q_REGULATIONS_DIMMED_HINT")}
        </p>
      )}
    </div>
  )
}

/** A stored request's regulations: the pills, plus Edit for whoever may process
 *  requests, which swaps in the picker and saves the keys (PATCH). */
export function RecordRegulations({
  row,
  canEdit,
}: {
  row: Pick<
    QuestionnaireRow,
    "id" | "regulationKeys" | "regulationKeysConfirmed" | "unmappedFrameworks"
  >
  canEdit: boolean
}) {
  const { translate } = useTranslation()
  const { mutateAsync: patchQuestionnaire, isPending } = usePatchQuestionnaire()
  const [draft, setDraft] = useState<string[] | null>(null)

  if (draft !== null) {
    return (
      <div className="flex flex-col items-end gap-2">
        <RegulationKeysPicker
          value={draft}
          onChange={setDraft}
          disabled={isPending}
        />
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={isPending}
            onClick={() => setDraft(null)}
          >
            {translate("MC_Q_REGULATIONS_CANCEL")}
          </Button>
          <Button
            size="sm"
            disabled={isPending}
            onClick={async () => {
              try {
                await patchQuestionnaire({
                  id: row.id,
                  body: { regulationKeys: draft },
                })
                setDraft(null)
              } catch (error) {
                toastApiError(error, translate, "MC_Q_REGULATIONS_SAVE_FAILED")
              }
            }}
          >
            {translate("MC_Q_REGULATIONS_SAVE")}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex flex-wrap items-center justify-end gap-[4px]">
        <RequestRegulationPills keys={row.regulationKeys} />
        <UnmappedFrameworkPills values={row.unmappedFrameworks} />
        {!row.regulationKeysConfirmed && row.regulationKeys.length > 0 && (
          <span className="text-[11px] text-grey5 italic">
            {translate("MC_Q_REGULATIONS_DETECTED")}
          </span>
        )}
        {canEdit && (
          <Button
            variant="ghost"
            size="sm"
            className="h-[22px] px-2 text-[11.5px]"
            onClick={() => setDraft([...row.regulationKeys])}
          >
            {translate("MC_Q_REGULATIONS_EDIT")}
          </Button>
        )}
      </div>
      {/* Read from the wording: Annex XVII may show although the request only named PFAS. */}
      {!row.regulationKeysConfirmed && (
        <PfasNeedsAnnexXviiNote keys={row.regulationKeys} readOnly />
      )}
    </div>
  )
}
